/**
 * useClarifyingQuestions - Polls the LLM for short clarifying questions
 * a clinician should ask the patient, while a ChartMind encounter is being
 * recorded. Refreshes every POLL_INTERVAL_MS so the visible list stays
 * relevant as the transcript grows.
 *
 * The same call also returns contradictions — where what the patient is saying conflicts with
 * their imported chart record, or with what they said earlier in this encounter. One call for
 * both because the two take identical input (this transcript, this patient's record) and a
 * contradiction is usually the reason for the question that follows it. A second poll would
 * re-pay those input tokens for nothing.
 *
 * Questions are replaced wholesale on every poll; contradictions accumulate, because a flag
 * has to keep its identity long enough to be struck through when the patient clarifies. That
 * bookkeeping lives in utils/contradictions.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { invokeLLMPrompt } from "services/llmService";
import { getLanguageName } from "constants/languages";
import { patientContextPayloadKey } from "utils/patientContext";
import {
  contradictionsKey,
  formatKnownContradictionsBlock,
  mergeContradictions,
  parseContradictions,
  toggleContradictionResolved as toggleResolved,
} from "utils/contradictions";
import { useAuth } from "hooks/useAuth";

const QUERY_ID = "chartmind-clarifying-questions";
const POLL_INTERVAL_MS = 10000;
const MIN_TRANSCRIPT_LENGTH = 50;

const cleanQuestion = (raw) => {
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    return (raw.question || raw.text || raw.prompt || "").toString().trim();
  }
  return "";
};

const QUESTION_KEYS = [
  "clarifying_questions",
  "clarifyingQuestions",
  "questions",
  "clarifications",
  "suggestedQuestions",
  "suggested_questions",
];

const cleanList = (arr) =>
  Array.isArray(arr) ? arr.map(cleanQuestion).filter(Boolean) : [];

// LLMs sometimes prepend prose before the JSON object. Extract the JSON
// candidate by preferring a fenced ```json``` block; otherwise grab
// everything between the first '{' and the last '}'.
const extractJsonCandidate = (text) => {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenced) return fenced[1].trim();

  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  if (first !== -1 && last > first) return text.slice(first, last + 1);

  return null;
};

const questionsFromParsed = (parsed) => {
  if (Array.isArray(parsed)) {
    const cleaned = cleanList(parsed);
    if (cleaned.length > 0) return cleaned;
  }

  if (parsed && typeof parsed === "object") {
    for (const key of QUESTION_KEYS) {
      const cleaned = cleanList(parsed[key]);
      if (cleaned.length > 0) return cleaned;
    }
  }

  return [];
};

/**
 * Both halves of one response. The JSON is extracted and parsed once and handed to each
 * half, so a model that wraps its object in prose costs us one recovery rather than two.
 *
 * Only questions fall back to line parsing — a bare list of lines is a plausible way to
 * answer "give me questions" and worth rescuing. Contradictions carry structure (id, status,
 * which side the conflict is with) that no line-parse can reconstruct, so without valid JSON
 * there are none, which is also what a prompt predating this feature returns.
 */
const parseResponse = (output) => {
  if (!output) return { questions: [], contradictions: [] };
  const trimmed = output.trim();

  const jsonStr = extractJsonCandidate(trimmed);
  let parsed = null;
  if (jsonStr) {
    try {
      parsed = JSON.parse(jsonStr);
    } catch (err) {
      console.warn(
        "[Clarifying Questions] JSON parse failed, falling back to line parsing:",
        err?.message || err,
      );
    }
  }

  const questions = questionsFromParsed(parsed);

  return {
    questions: questions.length
      ? questions
      : trimmed
        .split("\n")
        .map((line) => line.replace(/^[\s\-*\d.)]+/, "").trim())
        .filter((line) => line.length > 0 && line.endsWith("?")),
    contradictions: parseContradictions(parsed),
  };
};

const useClarifyingQuestions = ({
  transcript,
  isRecording,
  enabled = true,
  patientContext = null,
  // BCP-47 code from ChartMind's language selector — the language the encounter is being
  // conducted in, and therefore the language the questions have to come back in.
  language = "en-US",
}) => {
  const { organizationId } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [contradictions, setContradictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const transcriptRef = useRef(transcript);
  const lastQueriedTranscriptRef = useRef("");
  const inFlightRef = useRef(false);
  // Read through a ref for the same reason the transcript is: fetchQuestions fires from a
  // live-recording interval and must not be re-created every time the context changes.
  const patientContextRef = useRef(patientContext);
  // Same again for what we have already flagged. It goes into the outgoing prompt (so the
  // model reuses ids and can mark one resolved), and it is state the poll itself updates, so
  // reading it through a ref keeps fetchQuestions out of a self-triggering dependency loop.
  const contradictionsRef = useRef(contradictions);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    patientContextRef.current = patientContext;
  }, [patientContext]);

  useEffect(() => {
    contradictionsRef.current = contradictions;
  }, [contradictions]);

  const fetchQuestions = useCallback(async () => {
    const current = (transcriptRef.current || "").trim();
    if (current.length < MIN_TRANSCRIPT_LENGTH) {
      console.log("[Clarifying Questions] skip — transcript too short", {
        length: current.length,
        min: MIN_TRANSCRIPT_LENGTH,
      });
      return;
    }
    // Keyed on the patient context too, so importing a record mid-encounter re-asks
    // rather than sticking with questions generated before the record was available. The
    // known contradictions are in the key for the same reason: they are part of the outgoing
    // prompt, so a clinician striking one by hand makes the next poll worth firing.
    const knownContradictions = contradictionsRef.current;
    const requestKey = [
      current,
      patientContextPayloadKey(patientContextRef.current),
      contradictionsKey(knownContradictions),
    ].join("::");
    if (requestKey === lastQueriedTranscriptRef.current) {
      console.log("[Clarifying Questions] skip — transcript and patient context unchanged");
      return;
    }
    if (inFlightRef.current) {
      console.log("[Clarifying Questions] skip — request already in flight");
      return;
    }

    inFlightRef.current = true;
    lastQueriedTranscriptRef.current = requestKey;
    setLoading(true);
    setError(null);

    console.log("[Clarifying Questions] → firing request", {
      transcriptLength: current.length,
      transcriptPreview: current.slice(0, 120),
    });

    const knownBlock = formatKnownContradictionsBlock(knownContradictions);

    try {
      const result = await invokeLLMPrompt({
        promptId: QUERY_ID,
        // ⚠️ THE OUTPUT LANGUAGE IS STATED EXPLICITLY, and it has to be. Server-side
        // transcription auto-detects per segment, so a garbled or noisy couple of seconds can
        // come back as Hebrew or Korean in the middle of an English encounter. The model then
        // reasonably answers in the language it sees, and a clinician gets their clarifying
        // questions in Hebrew. Naming the language ChartMind's selector is set to pins the
        // output regardless of what junk survived transcription.
        userMessage: `The encounter is being conducted in ${getLanguageName(
          languageRef.current,
        )}. Write every question and every contradiction in ${getLanguageName(
          languageRef.current,
        )}, even if parts of the transcript below appear in another language.\n\nPATIENT ENCOUNTER TRANSCRIPT:\n\n${current}${
          knownBlock ? `\n\n${knownBlock}` : ""
        }`,
        organizationId,
        // So the model stops asking about medications and allergies the record already
        // answers, and can probe the gaps instead.
        patientContext: patientContextRef.current,
      });
      const rawOutput = result?.output;
      console.log("[Clarifying Questions] ← raw LLM response:\n", rawOutput);
      console.log("[Clarifying Questions] response meta:", result?.meta || null);

      const parsed = parseResponse(rawOutput);
      console.log("[Clarifying Questions] parsed questions:", parsed.questions);
      console.log("[Clarifying Questions] parsed contradictions:", parsed.contradictions);

      if (parsed.questions.length > 0) {
        setQuestions(parsed.questions);
      } else {
        console.warn(
          "[Clarifying Questions] parser returned 0 questions — keeping previous list",
        );
      }

      // Merged rather than replaced, and merged even when the response is empty: a flag the
      // model stopped mentioning is kept, and one it now calls resolved gets struck through.
      // See utils/contradictions for the rules.
      setContradictions((prev) => mergeContradictions(prev, parsed.contradictions));
    } catch (err) {
      console.error("[Clarifying Questions] error:", err);
      setError(err?.message || "Failed to generate clarifying questions");
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!isRecording || !enabled) {
      console.log(
        "[Clarifying Questions] polling disabled",
        { isRecording, enabled },
      );
      return undefined;
    }
    fetchQuestions();
    const id = setInterval(fetchQuestions, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isRecording, enabled, fetchQuestions]);

  // When recording stops, allow a fresh fetch on the next session without
  // throwing away the questions already on screen.
  useEffect(() => {
    if (!isRecording) lastQueriedTranscriptRef.current = "";
  }, [isRecording]);

  const clear = useCallback(() => {
    setQuestions([]);
    setContradictions([]);
    setError(null);
    lastQueriedTranscriptRef.current = "";
  }, []);

  // The clinician's own verdict on a flag, in both directions. They are in the room; the model
  // is reading a transcript with no speaker labels, so this outranks it (utils/contradictions).
  const toggleContradiction = useCallback((id) => {
    setContradictions((prev) => toggleResolved(prev, id));
  }, []);

  return {
    questions,
    contradictions,
    toggleContradictionResolved: toggleContradiction,
    loading,
    error,
    clear,
  };
};

export default useClarifyingQuestions;
